import React, { useState, useEffect } from "react";
import { Spin, Tag } from "antd";
import { TRANSACTION_TYPES, TRANSACTION_TYPE_LABELS, TRANSACTION_TABLE_MAP, TRANSACTION_MIGRATION_STATUS, TRANSACTION_MIGRATION_STATUS_LABELS, TRANSACTION_MIGRATION_STATUS_COLORS } from "../constants";
import TransactionStatusTag from "./TransactionStatusTag";
import TransactionEditPopover from "./TransactionEditPopover";
import useFlightTransactions from "../hooks/useFlightTransactions";
import { useAuth } from "../../../../hooks/useAuth";
import "./TransactionStyles.css";

/**
 * 事务单元格组件
 * 用于在航班列表中显示事务状态并提供交互
 *
 * @param {number} flightId - 航班ID
 * @param {string} transactionType - 事务类型
 * @param {Object} flight - 航班对象
 * @param {function} onSuccess - 操作成功后的回调函数，传递 flightId
 * @param {boolean} [compact=false] - 是否使用紧凑模式
 */
const TransactionCell = ({ flightId, transactionType, flight, onSuccess, compact = false }) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const { hasPermission } = useAuth();

    // 检查用户是否有事务操作权限
    const canEditTransactions = hasPermission("flight_operation:update");

    // 检查航班是否已包含预加载的事务数据
    const hasPreloadedData = flight && flight.transactions;

    // 从预加载数据中获取事务列表
    let transactions = [];
    let currentTransaction = null;
    let loading = false;

    if (hasPreloadedData) {
        // 根据事务类型从flight.transactions中获取对应类型的事务列表
        const transactionKey = TRANSACTION_TABLE_MAP[transactionType]
            ? `${TRANSACTION_TABLE_MAP[transactionType]}s` // 例如: fuel_request -> fuel_requests
            : null;

        if (transactionKey && flight.transactions && flight.transactions[transactionKey]) {
            transactions = flight.transactions[transactionKey];
            // 当前事务实例(最新的一条)
            currentTransaction = transactions && transactions.length > 0 ? transactions[0] : null;
        }
    }

    // 只有当需要编辑或查看详情时才创建hook实例，完全避免不必要的API调用
    // 这里使用true作为skipInitialFetch参数，确保不会自动发起API请求
    const { createTransaction, updateTransaction, fetchTransactions } = useFlightTransactions(flightId, transactionType, true);

    // 确保事务类型有效
    const isValidType = Object.values(TRANSACTION_TYPES).includes(transactionType);
    if (!isValidType) {
        return <div>无效事务类型</div>;
    }

    // 处理Popover可见性变更
    const handleVisibleChange = (visible) => {
        setPopoverVisible(visible);
    };

    // 处理操作成功
    const handleSuccess = (flightId) => {
        if (onSuccess && typeof onSuccess === "function") {
            onSuccess(flightId);
        }
    };

    // 处理鼠标悬停状态
    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    // 渲染加载状态
    if (loading) {
        return (
            <div className="transaction-cell transaction-cell-loading">
                <Spin size="small" />
            </div>
        );
    }

    // 渲染单元格内容
    const renderCellContent = () => {
        // 新增按钮 - 当没有事务时显示，且仅在悬停时显示
        if (!currentTransaction) {
            return (
                <div className="transaction-empty-container">
                    {isHovering && canEditTransactions ? (
                        <div className="transaction-hint">
                            <span className="transaction-hint-text">发起{TRANSACTION_TYPE_LABELS[transactionType].slice(-2)}</span>
                        </div>
                    ) : (
                        <span className="transaction-empty-placeholder">-</span>
                    )}
                </div>
            );
        }

        // 已有事务状态 - 根据迁移状态决定显示内容和样式
        const migrationStatus = currentTransaction.migration_status;

        // 如果是 REJECTED 状态，当做没有事务处理
        if (migrationStatus === TRANSACTION_MIGRATION_STATUS.REJECTED) {
            return (
                <div className="transaction-empty-container">
                    {isHovering && canEditTransactions ? (
                        <div className="transaction-hint">
                            <span className="transaction-hint-text">发起{TRANSACTION_TYPE_LABELS[transactionType].slice(-2)}</span>
                        </div>
                    ) : (
                        <span className="transaction-empty-placeholder">-</span>
                    )}
                </div>
            );
        }

        // 显示原来的事务状态，但如果是 PENDING_REVIEW 状态则增加特殊样式
        const isPendingReview = migrationStatus === TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW;

        return (
            <div className={`transaction-status-container ${isPendingReview ? "pending-migration-review" : ""} ${compact ? "transaction-status-compact" : ""}`}>
                <TransactionStatusTag status={currentTransaction.status} type={transactionType} size={compact ? "small" : "default"} showIcon={!compact} />
                {isPendingReview && !compact && (
                    <Tag color={TRANSACTION_MIGRATION_STATUS_COLORS.PENDING_REVIEW} size="small" style={{ marginLeft: 4 }}>
                        待检
                    </Tag>
                )}
            </div>
        );
    };

    // 如果没有权限，只显示状态，不提供编辑功能
    if (!canEditTransactions) {
        return <div className="transaction-cell-wrapper">{renderCellContent()}</div>;
    }

    return (
        <div className="transaction-cell-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <TransactionEditPopover flightId={flightId} transactionType={transactionType} transaction={currentTransaction} visible={popoverVisible} onVisibleChange={handleVisibleChange} trigger="click" onSuccess={handleSuccess} createTransaction={createTransaction} updateTransaction={updateTransaction} fetchTransactions={fetchTransactions} flight={flight}>
                {renderCellContent()}
            </TransactionEditPopover>
        </div>
    );
};

export default TransactionCell;
